import React, { useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';

import { Stack, Label, CommandButton } from '@bingads-webui-react/fabric-control';
import {
  ContextualMenuItemType,
} from '@fluentui/react/lib/ContextualMenu';
import { withCustomValidation, withError } from '@bingads-webui-react/validation';
import { withControlErrorHandling, withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';
import { useAccountPropertyRepository } from './component/use-account-property-repository';

import {
  campaignThirdPartyVendors,
  vendorNameKey,
  settingEnabledKey,
  thirdPartySettingValidatorMap,
  createVerifiedTrackingSettingViewModel,
  reachVendors,
  verificationVendors,
  campaignThirdPartyVendorNames,
  VerifiedTrackingSettingKey,
  isVendorSelectedInSetting,
} from '../model/campaign-third-party-vendor-setting';

import { ThirdPartyTrackingSetting } from './component/third-party-vendor-setting';
import { ThirdPartyTrackingRemoveAllButton } from './component/third-party-measurement-remove-all-button';
import { thridPartyMeasurementTrackingStyles } from '../styles/third-party-measurement-setting-styles';

export const formatVendorSettings = ({ verifiedTrackingSettingsValidationProps, i18n }) => {
  const verifiedTrackingSettings = _.result(verifiedTrackingSettingsValidationProps, ['data', VerifiedTrackingSettingKey], []);
  const isSettingsAllDisabled = settings => _.every(settings, setting => !_.get(setting, [settingEnabledKey], true));
  const settingEmpty = _.isEmpty(verifiedTrackingSettings) || isSettingsAllDisabled(verifiedTrackingSettings);

  const getVendorNames = (settings) => {
    const validSettings = _.filter(settings, setting => _.get(setting, [settingEnabledKey], true));
    const selectedVendors = _.map(validSettings, setting => _.get(setting, [vendorNameKey], ''));
    const vendorNameArr = _.map(selectedVendors, vendor => i18n.getString(campaignThirdPartyVendorNames[vendor]));
    return vendorNameArr.join(', ');
  };

  return (
    <div white-space="pre">
      {
        settingEmpty ?
          i18n.getString(_TL_('No third-party vendor selected')) :
          (
            <div>
              {i18n.getString(_TL_('Added: ')) + getVendorNames(verifiedTrackingSettings)}
            </div>
          )
      }
    </div>
  );
};

export const ThirdPartyMeasurementV3 = (props) => {
  const {
    i18n,
    updateValidation,
    validationProps,
    overrideStyles,
    IsMSANISPOTEnabled,
    EnableDoubleVerify,
    cid,
    aid,
  } = props;

  const [doubleVerifyLinkEstablished] = useAccountPropertyRepository({ cid, aid });

  const [settings, setSettings] = useState([]);
  const styles = useMemo(() => ({
    ...thridPartyMeasurementTrackingStyles,
    ...overrideStyles,
  }), [overrideStyles]);
  const verifiedTrackingData = useMemo(
    () => {
      const data = _.result(validationProps, ['data'], null);
      if (_.isNull(data)) {
        return null;
      }
      return _.result(validationProps, ['data', VerifiedTrackingSettingKey], []);
    }
    , [validationProps]
  );

  // Set settings on initailization
  useEffect(() => {
    if (_.isNull(verifiedTrackingData) || _.isUndefined(verifiedTrackingData)) return;
    const settingsToUpdate = [];
    _.each(campaignThirdPartyVendors, (vendor) => {
      const vendorData = verifiedTrackingData.find(setting => setting[vendorNameKey] === vendor);
      if (!_.isEmpty(vendorData)) {
        settingsToUpdate.push({
          [settingEnabledKey]: true,
          ...vendorData,
        });
      } else {
        settingsToUpdate.push({
          [settingEnabledKey]: false,
          [vendorNameKey]: vendor,
        });
      }
    });
    setSettings(settingsToUpdate);
  }, [verifiedTrackingData]);

  // Update to outside when settings change and valiate
  useEffect(() => {
    if (_.isEmpty(settings)) {
      return;
    }
    // Validate each vendor settings
    const errorsToUpdate = [];
    const validatedSettingsArray = [];
    _.each(settings, (setting) => {
      const vendor = setting[vendorNameKey];
      if (_.isEmpty(vendor)) {
        return;
      }
      let validatedVendorSetting = { [vendorNameKey]: vendor };
      const settingEnabled = _.get(setting, [settingEnabledKey], false);
      if (settingEnabled) {
        // Validate each setting
        if (_.isFunction(thirdPartySettingValidatorMap[vendor])) {
          const currentVendorErrors = thirdPartySettingValidatorMap[vendor]({ setting, i18n, doubleVerifyLinkEstablished });
          if (!_.isEmpty(currentVendorErrors)) {
            errorsToUpdate.push(...currentVendorErrors);
          }
        }

        validatedVendorSetting = {
          ...validatedVendorSetting,
          ...setting,
          [settingEnabledKey]: true,
        };
      } else {
        validatedVendorSetting[settingEnabledKey] = false;
      }
      validatedSettingsArray.push(validatedVendorSetting);
    });
    // TODO: flatten the errors
    updateValidation(errorsToUpdate, createVerifiedTrackingSettingViewModel(validatedSettingsArray));
  }, [settings, updateValidation, i18n, doubleVerifyLinkEstablished]);

  const addSetting = useCallback((vendorName) => {
    setSettings((originalSettings) => {
      const updatedSettings = [];
      let vendorFound = false;
      _.each(originalSettings, (setting) => {
        if (_.get(setting, [vendorNameKey], '') !== vendorName) {
          updatedSettings.push({ ...setting });
        } else {
          // If already existing a vendor setting, enable it
          updatedSettings.push({
            ...setting,
            [settingEnabledKey]: true,
          });
          vendorFound = true;
        }
      });

      return vendorFound ? updatedSettings : [
        ...updatedSettings,
        {
          [vendorNameKey]: vendorName,
          [settingEnabledKey]: true,
        },
      ];
    });
  }, [setSettings]);

  const removeSetting = useCallback((index) => {
    setSettings((originalSettings) => {
      const newSettings = [];
      _.each(originalSettings, (setting, i) => {
        if (i === index) {
          newSettings.push({
            ...setting,
            [settingEnabledKey]: false,
          });
        } else {
          newSettings.push({ ...setting });
        }
      });
      return newSettings;
    });
  }, [setSettings]);

  const removeAllVendorSettings = useCallback((vendorNameArr) => {
    setSettings((originalSettings) => {
      const newSettings = [];
      _.each(originalSettings, (setting) => {
        const currentVendorName = _.get(setting, [vendorNameKey], '');
        if (_.includes(vendorNameArr, currentVendorName)) {
          newSettings.push({
            ...setting,
            [settingEnabledKey]: false,
          });
        } else {
          newSettings.push({ ...setting });
        }
      });
      return newSettings;
    });
  }, []);

  const setSetting = useCallback((index, updatedSetting) => {
    setSettings(oldSettings => _.map(oldSettings, (s, i) => (i === index ? updatedSetting : s)));
  }, [setSettings]);

  const onRenderReachSettings = useCallback(() => {
    const reachSettings = [];
    _.each(settings, (setting, index) => {
      if (_.get(setting, [settingEnabledKey], false) && _.contains(reachVendors, _.get(setting, [vendorNameKey], ''))) {
        reachSettings.push({
          ...setting,
          indexInSettings: index,
        });
      }
    });

    if (_.isEmpty(reachSettings)) {
      return null;
    }

    return (
      <div className="third-party-reach-settings">
        <Stack verticalAlign="center" horizontal className="third-party-measurement-reach-title">
          <Label>
            {i18n.getString(_TL_('Reach'))}
          </Label>
        </Stack>
        <ThirdPartyTrackingRemoveAllButton
          i18n={i18n}
          removeAllVendorSettings={() => removeAllVendorSettings(reachVendors)}
        />
        {reachSettings.map(setting => (
          <ThirdPartyTrackingSetting
            i18n={i18n}
            setting={setting}
            setSetting={updatedSetting => setSetting(setting.indexInSettings, updatedSetting)}
            removeSetting={() => removeSetting(setting.indexInSettings)}
          />
        ))}
      </div>
    );
  }, [settings, i18n, setSetting, removeSetting, removeAllVendorSettings]);

  const onRenderVerificationSettings = useCallback(() => {
    const verificationSettings = [];
    _.each(settings, (setting, index) => {
      if (_.get(setting, [settingEnabledKey], false) && _.contains(verificationVendors, _.get(setting, [vendorNameKey], ''))) {
        verificationSettings.push({
          ...setting,
          indexInSettings: index,
        });
      }
    });

    if (_.isEmpty(verificationSettings)) {
      return null;
    }

    return (
      <div className="third-party-reach-settings">
        <Stack verticalAlign="center" horizontal className="third-party-measurement-reach-title">
          <Label>
            {i18n.getString(_TL_('Measurement provider'))}
          </Label>
        </Stack>
        <ThirdPartyTrackingRemoveAllButton
          i18n={i18n}
          removeAllVendorSettings={() => removeAllVendorSettings(reachVendors)}
        />
        {verificationSettings.map(setting => (
          <ThirdPartyTrackingSetting
            i18n={i18n}
            setting={setting}
            setSetting={updatedSetting => setSetting(setting.indexInSettings, updatedSetting)}
            removeSetting={() => removeSetting(setting.indexInSettings)}
            doubleVerifyLinkEstablished={doubleVerifyLinkEstablished}
          />
        ))}
      </div>
    );
  }, [settings, i18n, setSetting, removeSetting, removeAllVendorSettings, doubleVerifyLinkEstablished]);

  const addVendorMenuProps = useMemo(() => {
    const itemsToPush = [];
    if (IsMSANISPOTEnabled) {
      itemsToPush.push({ key: 'ReachAndIncrementalityHeader', text: i18n.getString(_TL_('Reach vendor')), itemType: ContextualMenuItemType.Header });
      const ispotSelected = isVendorSelectedInSetting(settings, campaignThirdPartyVendors.ISPOT);
      itemsToPush.push({
        key: 'ISPOT', text: i18n.getString(_TL_('iSpot.tv')), onClick: () => addSetting(campaignThirdPartyVendors.ISPOT), disabled: ispotSelected,
      });
    }
    if (IsMSANISPOTEnabled && EnableDoubleVerify) {
      itemsToPush.push({ key: 'divider1', text: '-', itemType: ContextualMenuItemType.Divider });
    }
    if (EnableDoubleVerify) {
      itemsToPush.push({ key: 'VerificationHeader', text: i18n.getString(_TL_('Verification vendor')), itemType: ContextualMenuItemType.Header });
      const DVSelected = isVendorSelectedInSetting(settings, campaignThirdPartyVendors.DV);
      itemsToPush.push({
        key: 'DV', text: i18n.getString(_TL_('DoubleVerify')), onClick: () => addSetting(campaignThirdPartyVendors.DV), disabled: DVSelected,
      });
    }
    return {
      items: itemsToPush,
      styles: styles.thirdPartyMeasurementAddButton.dropdown,
      className: 'third-party-measurement-vendor-dropdown',
    };
  }, [i18n, addSetting, styles.thirdPartyMeasurementAddButton, IsMSANISPOTEnabled, EnableDoubleVerify, settings]);

  return (
    <div className="third-party-measurement-tile">
      {onRenderReachSettings()}
      {onRenderVerificationSettings()}
      <Stack verticalAlign="center" horizontal>
        <CommandButton
          className="third-party-measurement-add-button"
          text={i18n.getString(_TL_('Add'))}
          iconProps={{ iconName: 'Add' }}
          menuProps={addVendorMenuProps}
          styles={
            styles.thirdPartyMeasurementAddButton
          }
        />
      </Stack>
    </div>
  );
};

ThirdPartyMeasurementV3.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      VerifiedTrackingSettings: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }).isRequired,
  overrideStyles: PropTypes.shape({}),
  IsMSANISPOTEnabled: PropTypes.bool,
  EnableDoubleVerify: PropTypes.bool,
  cid: PropTypes.string.isRequired,
  aid: PropTypes.string.isRequired,
};

ThirdPartyMeasurementV3.defaultProps = {
  overrideStyles: {},
  IsMSANISPOTEnabled: false,
  EnableDoubleVerify: false,
};

export const ThirdPartyMeasurementV3WithValidation = withControlErrorHandling(withUnvalidatedControlErrors(withError(withCustomValidation(ThirdPartyMeasurementV3))));
